import { SurveyRecipient } from "../types/survey";

export enum EmailValidationError {
  AlreadyInList = "ALREADY_IN_LIST",
  InvalidEmail = "INVALID_EMAIL",
  InvalidPhoneNumber = "INVALID_PHONE_NUMBER",
}

export const isEmail = (email: string) => {
  // eslint-disable-next-line no-useless-escape
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
};

export const isPhoneNumber = (phone: string) => {
  // Only allow numbers with + country code prefix, no spaces or separators

  // Pattern for + prefix: +[1-9][0-9]{6,14} - supports country codes 1-9999 with 7-15 total digits (min 7, max 15 for valid phone)
  const plusPattern = /^\+[1-9][0-9]{6,14}$/;

  return plusPattern.test(phone);
};

export const isInList = (item: string, list: string[]) => {
  return list.includes(item);
};

export const isDate = (date: string) => {
  // eslint-disable-next-line no-useless-escape
  return /(31[\/.](0[13578]|1[02])[\/.](18|19|20)[0-9]{2})|((29|30)[\/.](01|0[3-9]|1[1-2])[\/.](18|19|20)[0-9]{2})|((0[1-9]|1[0-9]|2[0-8])[\/.](0[1-9]|1[0-2])[\/.](18|19|20)[0-9]{2})|(29[\/.](02)[\/.](((18|19|20)(04|08|[2468][048]|[13579][26]))|2000))/.test(
    date
  );
};

export const isValidEmail = (email: string, list: string[]) => {
  if (isInList(email, list)) {
    return false;
  }

  if (!isEmail(email)) {
    return false;
  }

  return true;
};

export const validatePhoneNumber = (phone: string) => {
  if (!phone || phone.trim() === "") {
    return null; // Phone number is optional, so empty is valid
  }

  if (!isPhoneNumber(phone)) {
    return EmailValidationError.InvalidPhoneNumber;
  }

  return null; // Valid phone number
};

export const validateEmails = (
  existingEmailList: string[],
  newEmails: string[]
) => {
  const duplicates: string[] = [];
  const toBeAdded: string[] = [];
  const invalidEmails: string[] = [];

  newEmails.forEach((email: string) => {
    if (
      isInList(email, existingEmailList) ||
      toBeAdded.includes(email) ||
      duplicates.includes(email)
    ) {
      duplicates.push(email);
    } else if (!isEmail(email)) {
      invalidEmails.push(email);
    } else {
      toBeAdded.push(email);
    }
  });
  return { duplicates, toBeAdded, invalidEmails };
};

export const hasDuplicates = (list: string[]) => {
  return new Set(list).size !== list.length;
};

export const validateEmail = (email: string, list: string[]) => {
  if (!isEmail(email)) {
    return EmailValidationError.InvalidEmail;
  } else if (hasDuplicates(list)) {
    return EmailValidationError.AlreadyInList;
  }
};

export const validateRecipients = (
  existingRecipients: Array<{ email: string; phone_number?: string }>,
  newRecipients: Array<{ email: string; phone_number?: string }>
) => {
  const duplicates: string[] = [];
  const toBeAdded: Array<{ email: string; phone_number?: string }> = [];
  const invalidEmails: string[] = [];
  const invalidPhones: SurveyRecipient[] = [];

  const existingEmailList = existingRecipients.map((r) => r.email);

  newRecipients.forEach((recipient) => {
    const { email, phone_number } = recipient;

    // Check for email duplicates
    if (
      isInList(email, existingEmailList) ||
      toBeAdded.some((r) => r.email === email) ||
      duplicates.includes(email)
    ) {
      duplicates.push(email);
      return;
    }

    // Validate email
    if (!isEmail(email)) {
      invalidEmails.push(email);
      return;
    }

    // Validate phone number if provided
    if (phone_number && phone_number.trim() !== "") {
      const phoneValidationError = validatePhoneNumber(phone_number);
      if (phoneValidationError) {
        invalidPhones.push(recipient);
        return;
      }
    }

    // If all validations pass, add to toBeAdded
    toBeAdded.push(recipient);
  });

  return { duplicates, toBeAdded, invalidEmails, invalidPhones };
};

export const filterEmptyEmails = (list: string[]) => {
  return list.filter((email) => email !== "");
};
